import { useEffect, useMemo, useState } from 'react'
import { useQueries, useQuery } from '@tanstack/react-query'
import {
  addMonths, addWeeks, addYears, differenceInCalendarDays, eachDayOfInterval, eachMonthOfInterval,
  endOfMonth, endOfWeek, endOfYear, format, getDay, isAfter, isBefore, min as minDate, parseISO,
  startOfMonth, startOfWeek, startOfYear, subDays, subMonths,
} from 'date-fns'
import {
  CalendarDays, CalendarRange, ChevronLeft, ChevronRight, Download, LayoutGrid, List, X,
} from 'lucide-react'
import StatTile, { type Tone } from '@/components/dashboard/StatTile'
import SectionCard from '@/components/dashboard/SectionCard'
import { getMyAttendanceOverview, type MyAttendanceDay, type MyAttendanceOverview } from '@/api/attendance'
import { getEligibleTypes, type LeaveTypeConfig } from '@/api/leaves'
import { listHolidays } from '@/api/holidays'
import { useAuthStore } from '@/stores/authStore'

/* ─── Day kinds ─────────────────────────────────────────────────────────────── */

type Kind = 'present' | 'late' | 'wfh' | 'leave' | 'absent' | 'missing' | 'holiday' | 'off' | 'upcoming' | 'norecord'

const KIND: Record<Kind, { label: string; cell: string; dot: string; badge: string }> = {
  present: { label: 'Present', cell: 'bg-emerald-100 text-emerald-900', dot: 'bg-emerald-400', badge: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
  late: { label: 'Late', cell: 'bg-amber-100 text-amber-900', dot: 'bg-amber-400', badge: 'bg-amber-50 text-amber-700 ring-amber-200' },
  wfh: { label: 'WFH', cell: 'bg-teal-100 text-teal-900', dot: 'bg-teal-400', badge: 'bg-teal-50 text-teal-700 ring-teal-200' },
  leave: { label: 'Leave', cell: 'bg-violet-100 text-violet-900', dot: 'bg-violet-400', badge: 'bg-violet-50 text-violet-700 ring-violet-200' },
  absent: { label: 'Absent', cell: 'bg-rose-100 text-rose-900', dot: 'bg-rose-400', badge: 'bg-rose-50 text-rose-700 ring-rose-200' },
  missing: { label: 'Missing check-out', cell: 'bg-orange-100 text-orange-900', dot: 'bg-orange-400', badge: 'bg-orange-50 text-orange-700 ring-orange-200' },
  holiday: { label: 'Holiday', cell: 'bg-indigo-100 text-indigo-900', dot: 'bg-indigo-400', badge: 'bg-indigo-50 text-indigo-700 ring-indigo-200' },
  off: { label: 'Day off', cell: 'bg-gray-100 text-gray-500', dot: 'bg-gray-300', badge: 'bg-gray-50 text-gray-500 ring-gray-200' },
  upcoming: { label: 'Upcoming', cell: 'bg-white text-gray-300 border border-dashed border-gray-200', dot: 'bg-white border border-gray-300', badge: 'bg-white text-gray-400 ring-gray-200' },
  norecord: { label: 'No record', cell: 'bg-gray-50 text-gray-300', dot: 'bg-gray-100', badge: 'bg-gray-50 text-gray-400 ring-gray-200' },
}
const LEGEND: Kind[] = ['present', 'late', 'wfh', 'leave', 'absent', 'missing', 'holiday', 'off']

function kindOf(d: MyAttendanceDay | undefined, key: string, today: string): Kind {
  if (key > today) return 'upcoming'
  if (!d) return 'norecord'
  if (d.is_active && d.date < today) return 'missing'
  if (d.status === 'wfh') return 'wfh'
  if (d.status === 'leave') return 'leave'
  if (d.status === 'absent') return 'absent'
  if (d.status === 'holiday') return 'holiday'
  if (d.status === 'off') return 'off'
  return d.is_late ? 'late' : 'present'
}

/* ─── Periods ───────────────────────────────────────────────────────────────── */

type Mode = 'week' | 'month' | 'year' | 'custom'
const MAX_CUSTOM_DAYS = 366
const MODE_KEY = 'attendance-history-mode'
const iso = (d: Date) => format(d, 'yyyy-MM-dd')

function periodFor(mode: Mode, anchor: Date): [Date, Date] {
  if (mode === 'week') return [startOfWeek(anchor, { weekStartsOn: 1 }), endOfWeek(anchor, { weekStartsOn: 1 })]
  if (mode === 'month') return [startOfMonth(anchor), endOfMonth(anchor)]
  return [startOfYear(anchor), endOfYear(anchor)]
}

function periodLabel(mode: Mode, start: Date, end: Date) {
  if (mode === 'week') {
    return start.getMonth() === end.getMonth()
      ? `${format(start, 'd')} – ${format(end, 'd MMM yyyy')}`
      : `${format(start, 'd MMM')} – ${format(end, 'd MMM yyyy')}`
  }
  if (mode === 'month') return format(start, 'MMMM yyyy')
  if (mode === 'year') return format(start, 'yyyy')
  return `${format(start, 'd MMM yyyy')} – ${format(end, 'd MMM yyyy')}`
}

/* ─── Formatting ────────────────────────────────────────────────────────────── */

function useTimeFmt() {
  const { user } = useAuthStore()
  return (isoStr?: string | null) => {
    if (!isoStr) return '—'
    try {
      return new Date(isoStr).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: user?.timezone || undefined })
    } catch {
      return format(new Date(isoStr), 'h:mm a')
    }
  }
}

function hm(minutes?: number | null) {
  if (!minutes || minutes <= 0) return '—'
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return h ? (m ? `${h}h ${m}m` : `${h}h`) : `${m}m`
}

function humanize(key: string) {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

/* ─── Component ─────────────────────────────────────────────────────────────── */

/** The employee's own attendance over any period — a week, month, year or custom
 *  range — as a colour-coded calendar or a day-by-day list, with totals. */
export default function AttendanceHistory() {
  const { user } = useAuthStore()
  const fmtTime = useTimeFmt()
  const todayDate = new Date()
  const today = iso(todayDate)

  const [mode, setModeState] = useState<Mode>(() => {
    try {
      const saved = localStorage.getItem(MODE_KEY) as Mode | null
      return saved && ['week', 'month', 'year', 'custom'].includes(saved) ? saved : 'month'
    } catch {
      return 'month'
    }
  })
  const setMode = (m: Mode) => {
    setModeState(m)
    try { localStorage.setItem(MODE_KEY, m) } catch { /* private mode */ }
  }
  const [anchor, setAnchor] = useState(todayDate)
  const [customStart, setCustomStart] = useState(iso(subMonths(todayDate, 3)))
  const [customEnd, setCustomEnd] = useState(today)
  const [view, setView] = useState<'calendar' | 'list'>('calendar')
  const [filter, setFilter] = useState<Kind | 'all'>('all')
  const [selected, setSelected] = useState<string | null>(null)

  const [start, end] = useMemo<[Date, Date]>(() => {
    if (mode !== 'custom') return periodFor(mode, anchor)
    let s = parseISO(customStart)
    let e = parseISO(customEnd)
    if (isAfter(s, e)) [s, e] = [e, s]
    if (differenceInCalendarDays(e, s) >= MAX_CUSTOM_DAYS) s = subDays(e, MAX_CUSTOM_DAYS - 1)
    return [s, e]
  }, [mode, anchor, customStart, customEnd])

  // Only ask for days that have happened.
  const fetchEnd = minDate([end, todayDate])
  const hasPast = !isAfter(start, todayDate)
  const startStr = iso(start)
  const fetchEndStr = iso(fetchEnd)

  const { data, isLoading, isError } = useQuery<MyAttendanceOverview>({
    queryKey: ['attendance', 'me', 'history', startStr, fetchEndStr],
    queryFn: () => getMyAttendanceOverview(startStr, fetchEndStr),
    enabled: hasPast,
    staleTime: 60_000,
  })
  const { data: leaveTypes = [] } = useQuery<LeaveTypeConfig[]>({
    queryKey: ['leave-types', 'eligible', user?.id],
    queryFn: getEligibleTypes,
    enabled: !!user?.id,
  })
  const years = Array.from(new Set([start.getFullYear(), end.getFullYear()]))
  const holidayQueries = useQueries({
    queries: years.map((y) => ({ queryKey: ['holidays', y], queryFn: () => listHolidays(y), staleTime: 5 * 60_000 })),
  })
  const holidayName = new Map<string, string>()
  holidayQueries.forEach((q) => q.data?.forEach((h) => holidayName.set(h.date, h.name)))

  const leaveLabel = (key?: string | null) =>
    key ? leaveTypes.find((t: LeaveTypeConfig) => t.key === key)?.label || humanize(key) : 'Leave'

  const byDate = useMemo(() => new Map<string, MyAttendanceDay>((data?.days || []).map((d: MyAttendanceDay) => [d.date, d] as [string, MyAttendanceDay])), [data])
  const allDays = useMemo(() => eachDayOfInterval({ start, end }).map(iso), [start, end])
  const kinds = useMemo(() => new Map<string, Kind>(allDays.map((k) => [k, kindOf(byDate.get(k), k, today)])), [allDays, byDate, today])

  const counts = useMemo(() => {
    const c: Record<string, number> = {}
    kinds.forEach((k) => { c[k] = (c[k] || 0) + 1 })
    return c
  }, [kinds])

  // Reset drill-down filter when the period changes.
  useEffect(() => { setFilter('all') }, [startStr, iso(end)]) // eslint-disable-line react-hooks/exhaustive-deps

  const step = (dir: 1 | -1) => {
    if (mode === 'week') setAnchor((a) => addWeeks(a, dir))
    else if (mode === 'month') setAnchor((a) => addMonths(a, dir))
    else if (mode === 'year') setAnchor((a) => addYears(a, dir))
  }
  const atCurrent = mode !== 'custom' && iso(periodFor(mode, anchor)[0]) === iso(periodFor(mode, todayDate)[0])

  const preset = (days: number | 'ytd') => {
    setCustomEnd(today)
    setCustomStart(days === 'ytd' ? iso(startOfYear(todayDate)) : iso(subDays(todayDate, days - 1)))
  }

  const pickTile = (k: Kind) => {
    setFilter((f) => (f === k ? 'all' : k))
    setView('list')
  }

  const listRows = allDays
    .filter((k) => k <= today)
    .filter((k) => (filter === 'all' ? kinds.get(k) !== 'norecord' : kinds.get(k) === filter))
    .reverse()

  const exportCsv = () => {
    const header = ['Date', 'Day', 'Status', 'Check in', 'Check out', 'Worked', 'Expected', 'Late (min)', 'Left early (min)']
    const rows = allDays.filter((k) => k <= today && kinds.get(k) !== 'norecord').map((k) => {
      const d = byDate.get(k)
      const kind = kinds.get(k)!
      const status = kind === 'leave' ? leaveLabel(d?.leave_type) : kind === 'holiday' ? `Holiday${holidayName.get(k) ? ` (${holidayName.get(k)})` : ''}` : KIND[kind].label
      return [k, format(parseISO(k), 'EEEE'), status, d?.checkin_time ? fmtTime(d.checkin_time) : '', d?.checkout_time ? fmtTime(d.checkout_time) : '',
        hm(d?.worked_minutes).replace('—', ''), hm(d?.expected_minutes).replace('—', ''), d?.late_minutes || '', d?.early_checkout_minutes || '']
    })
    const csv = [header, ...rows].map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n')
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }))
    const a = document.createElement('a')
    a.href = url
    a.download = `attendance_${startStr}_to_${iso(end)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const trackedFrom = data?.tracked_from
  const tiles: { kind: Kind; tone: Tone; value: number }[] = [
    { kind: 'present', tone: 'green', value: (counts.present || 0) + (counts.late || 0) },
    { kind: 'late', tone: 'amber', value: counts.late || 0 },
    { kind: 'leave', tone: 'purple', value: counts.leave || 0 },
    { kind: 'wfh', tone: 'teal', value: counts.wfh || 0 },
    { kind: 'absent', tone: 'red', value: counts.absent || 0 },
    { kind: 'missing', tone: 'orange', value: counts.missing || 0 },
    { kind: 'holiday', tone: 'indigo', value: counts.holiday || 0 },
  ]
  const avg = data && data.present_days ? Math.round(data.worked_minutes / data.present_days) : 0

  return (
    <div className="space-y-5">
      {/* ── Controls ── */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-3 sm:p-4 flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="inline-flex rounded-lg bg-gray-100 p-1 self-start">
          {(['week', 'month', 'year', 'custom'] as Mode[]).map((m) => (
            <button key={m} onClick={() => { setMode(m); setAnchor(new Date()) }}
              className={`px-3 sm:px-4 py-1.5 rounded-md text-sm font-medium capitalize transition-colors ${mode === m ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}>
              {m === 'week' ? 'Weekly' : m === 'month' ? 'Monthly' : m === 'year' ? 'Yearly' : 'Custom'}
            </button>
          ))}
        </div>

        {mode !== 'custom' ? (
          <div className="flex items-center gap-1 lg:ml-2">
            <button onClick={() => step(-1)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-600" aria-label="Previous period"><ChevronLeft className="h-5 w-5" /></button>
            <span className="min-w-[10.5rem] text-center text-sm font-semibold text-gray-900 tabular-nums">{periodLabel(mode, start, end)}</span>
            <button onClick={() => step(1)} disabled={atCurrent} className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 disabled:opacity-30 disabled:hover:bg-transparent" aria-label="Next period"><ChevronRight className="h-5 w-5" /></button>
            {!atCurrent && (
              <button onClick={() => setAnchor(new Date())} className="ml-1 px-2.5 py-1 rounded-lg text-xs font-medium text-brand hover:bg-brand/10">
                {mode === 'week' ? 'This week' : mode === 'month' ? 'This month' : 'This year'}
              </button>
            )}
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2 lg:ml-2">
            <input type="date" value={customStart} max={customEnd} onChange={(e) => e.target.value && setCustomStart(e.target.value)}
              className="rounded-lg border border-gray-200 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30" aria-label="From" />
            <span className="text-gray-400 text-sm">to</span>
            <input type="date" value={customEnd} min={customStart} max={today} onChange={(e) => e.target.value && setCustomEnd(e.target.value)}
              className="rounded-lg border border-gray-200 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30" aria-label="To" />
            <div className="flex gap-1">
              {([[30, '30 days'], [90, '3 months'], [365, '12 months'], ['ytd', 'This year']] as const).map(([v, l]) => (
                <button key={l} onClick={() => preset(v)} className="px-2 py-1 rounded-md text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-800">{l}</button>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 lg:ml-auto">
          <div className="inline-flex rounded-lg border border-gray-200 p-0.5">
            <button onClick={() => setView('calendar')} title="Calendar"
              className={`p-1.5 rounded-md ${view === 'calendar' ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-100'}`}><LayoutGrid className="h-4 w-4" /></button>
            <button onClick={() => setView('list')} title="List"
              className={`p-1.5 rounded-md ${view === 'list' ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-100'}`}><List className="h-4 w-4" /></button>
          </div>
          <button onClick={exportCsv} disabled={!data}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40">
            <Download className="h-4 w-4" /> Export
          </button>
        </div>
      </div>
      {mode === 'custom' && differenceInCalendarDays(parseISO(customEnd), parseISO(customStart)) >= MAX_CUSTOM_DAYS && (
        <p className="text-xs text-amber-700 -mt-3">Custom ranges are limited to one year — showing the last 366 days of your selection.</p>
      )}

      {/* ── Totals ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-3">
        {tiles.map((t) => (
          <StatTile key={t.kind} label={t.kind === 'present' ? 'Days present' : KIND[t.kind].label} value={isLoading ? '…' : t.value}
            tone={t.tone} active={filter === t.kind} onClick={() => pickTile(t.kind)} />
        ))}
        <StatTile label="Hours worked" value={isLoading ? '…' : hm(data?.worked_minutes)} tone="blue"
          tag={avg ? { label: `${hm(avg)}/day`, tone: 'blue' } : undefined} />
      </div>

      {trackedFrom && trackedFrom > startStr && trackedFrom <= iso(end) && (
        <div className="flex items-center gap-2 rounded-lg bg-gray-50 border border-gray-100 px-3 py-2 text-xs text-gray-500">
          <CalendarRange className="h-4 w-4 shrink-0" />
          Your attendance is recorded from <strong className="text-gray-700">{format(parseISO(trackedFrom), 'd MMM yyyy')}</strong> — earlier days show as “No record”.
        </div>
      )}

      {/* ── Results ── */}
      <SectionCard
        title={
          <span className="flex items-center gap-2">
            {view === 'calendar' ? <CalendarDays className="h-4 w-4 text-gray-400" /> : <List className="h-4 w-4 text-gray-400" />}
            {view === 'calendar' ? 'Calendar' : 'Day by day'}
            {view === 'list' && filter !== 'all' && (
              <button onClick={() => setFilter('all')} className={`ml-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ring-1 ${KIND[filter].badge}`}>
                {KIND[filter].label} <X className="h-3 w-3" />
              </button>
            )}
          </span>
        }
        action={
          view === 'calendar' ? (
            <div className="flex flex-wrap gap-x-3 gap-y-1">
              {LEGEND.map((k) => (
                <span key={k} className="inline-flex items-center gap-1.5 text-[11px] text-gray-500">
                  <span className={`h-2.5 w-2.5 rounded-sm ${KIND[k].dot}`} /> {KIND[k].label}
                </span>
              ))}
            </div>
          ) : undefined
        }
        bodyClassName={view === 'list' ? 'p-0' : ''}
      >
        {!hasPast ? (
          <p className="py-10 text-center text-sm text-gray-400">This period hasn't started yet.</p>
        ) : isError ? (
          <p className="py-10 text-center text-sm text-red-600">Couldn't load your attendance. Please try again.</p>
        ) : isLoading ? (
          <div className="py-16 flex justify-center"><div className="h-6 w-6 rounded-full border-2 border-brand border-t-transparent animate-spin" /></div>
        ) : view === 'calendar' ? (
          mode === 'week' ? (
            <WeekCards days={allDays} kinds={kinds} byDate={byDate} today={today} fmtTime={fmtTime} onPick={setSelected} leaveLabel={leaveLabel} holidayName={holidayName} />
          ) : mode === 'month' ? (
            <MonthGrid month={start} kinds={kinds} byDate={byDate} today={today} fmtTime={fmtTime} onPick={setSelected} leaveLabel={leaveLabel} holidayName={holidayName} />
          ) : (
            <YearGrid start={start} end={end} kinds={kinds} today={today} onPick={setSelected} byDate={byDate} leaveLabel={leaveLabel} holidayName={holidayName} />
          )
        ) : listRows.length === 0 ? (
          <p className="py-12 text-center text-sm text-gray-400">{filter === 'all' ? 'No attendance recorded in this period.' : `No ${KIND[filter].label.toLowerCase()} days in this period.`}</p>
        ) : (
          <DayTable rows={listRows} kinds={kinds} byDate={byDate} today={today} fmtTime={fmtTime} onPick={setSelected} leaveLabel={leaveLabel} holidayName={holidayName} />
        )}
      </SectionCard>

      {selected && (
        <DayDetail date={selected} day={byDate.get(selected)} kind={kinds.get(selected) || kindOf(byDate.get(selected), selected, today)}
          fmtTime={fmtTime} leaveLabel={leaveLabel} holiday={holidayName.get(selected)} onClose={() => setSelected(null)} />
      )}
    </div>
  )
}

/* ─── Shared bits ───────────────────────────────────────────────────────────── */

interface DayProps {
  kinds: Map<string, Kind>
  byDate: Map<string, MyAttendanceDay>
  today: string
  onPick: (d: string) => void
  leaveLabel: (k?: string | null) => string
  holidayName: Map<string, string>
}

function statusText(kind: Kind, d: MyAttendanceDay | undefined, leaveLabel: DayProps['leaveLabel'], holiday?: string) {
  if (kind === 'leave') return leaveLabel(d?.leave_type)
  if (kind === 'holiday') return holiday || 'Holiday'
  return KIND[kind].label
}

function Badge({ kind, children }: { kind: Kind; children: React.ReactNode }) {
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${KIND[kind].badge}`}>{children}</span>
}

function tooltip(key: string, kind: Kind, d: MyAttendanceDay | undefined, fmtTime: (s?: string | null) => string, leaveLabel: DayProps['leaveLabel'], holiday?: string) {
  return [
    format(parseISO(key), 'EEE, d MMM yyyy'),
    statusText(kind, d, leaveLabel, holiday),
    d?.checkin_time ? `In ${fmtTime(d.checkin_time)}` : null,
    d?.checkout_time ? `Out ${fmtTime(d.checkout_time)}` : null,
    d?.worked_minutes ? hm(d.worked_minutes) : null,
  ].filter(Boolean).join(' · ')
}

/* ─── Week ──────────────────────────────────────────────────────────────────── */

function WeekCards({ days, kinds, byDate, today, fmtTime, onPick, leaveLabel, holidayName }: DayProps & { days: string[]; fmtTime: (s?: string | null) => string }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-7 gap-2">
      {days.map((key) => {
        const kind = kinds.get(key)!
        const d = byDate.get(key)
        const clickable = kind !== 'upcoming' && kind !== 'norecord'
        return (
          <button key={key} disabled={!clickable} onClick={() => onPick(key)}
            className={`rounded-xl p-3 text-left transition-shadow ${KIND[kind].cell} ${clickable ? 'hover:shadow-md' : 'cursor-default'} ${key === today ? 'ring-2 ring-brand/60' : ''}`}>
            <div className="flex sm:block items-center justify-between">
              <p className="text-xs font-medium opacity-70">{format(parseISO(key), 'EEE')}</p>
              <p className="text-lg font-bold leading-tight">{format(parseISO(key), 'd MMM')}</p>
            </div>
            <p className="mt-1 text-xs font-semibold truncate">{kind === 'upcoming' ? '' : statusText(kind, d, leaveLabel, holidayName.get(key))}</p>
            {d?.checkin_time && (
              <div className="mt-2 space-y-0.5 text-[11px] opacity-80 tabular-nums">
                <p>In {fmtTime(d.checkin_time)}</p>
                <p>Out {d.checkout_time ? fmtTime(d.checkout_time) : '—'}</p>
                {d.worked_minutes > 0 && <p className="font-semibold">{hm(d.worked_minutes)}</p>}
              </div>
            )}
          </button>
        )
      })}
    </div>
  )
}

/* ─── Month ─────────────────────────────────────────────────────────────────── */

function MonthGrid({ month, kinds, byDate, today, fmtTime, onPick, leaveLabel, holidayName }: DayProps & { month: Date; fmtTime: (s?: string | null) => string }) {
  const all = eachDayOfInterval({ start: startOfMonth(month), end: endOfMonth(month) })
  const lead = (getDay(all[0]) + 6) % 7
  return (
    <div>
      <div className="grid grid-cols-7 gap-1.5 text-center text-[11px] font-medium uppercase tracking-wide text-gray-400 mb-1.5">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => <div key={d}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1.5">
        {Array.from({ length: lead }).map((_, i) => <div key={`pad-${i}`} />)}
        {all.map((date) => {
          const key = iso(date)
          const kind = kinds.get(key)!
          const d = byDate.get(key)
          const clickable = kind !== 'upcoming' && kind !== 'norecord'
          return (
            <button key={key} disabled={!clickable} onClick={() => onPick(key)}
              title={tooltip(key, kind, d, fmtTime, leaveLabel, holidayName.get(key))}
              className={`h-16 sm:h-20 rounded-lg p-1.5 sm:p-2 text-left flex flex-col transition-shadow ${KIND[kind].cell} ${clickable ? 'hover:shadow-md' : 'cursor-default'} ${key === today ? 'ring-2 ring-brand/60' : ''}`}>
              <span className="text-xs sm:text-sm font-bold">{format(date, 'd')}</span>
              {kind !== 'upcoming' && kind !== 'norecord' && (
                <span className="hidden sm:block text-[10px] font-medium truncate w-full">{statusText(kind, d, leaveLabel, holidayName.get(key))}</span>
              )}
              {d?.checkin_time && (
                <span className="hidden md:block mt-auto text-[10px] opacity-75 tabular-nums truncate w-full">
                  {fmtTime(d.checkin_time)}{d.checkout_time ? ` – ${fmtTime(d.checkout_time)}` : ''}
                </span>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}

/* ─── Year / custom range ───────────────────────────────────────────────────── */

function YearGrid({ start, end, kinds, today, onPick, byDate, leaveLabel, holidayName }: DayProps & { start: Date; end: Date }) {
  const fmtTime = useTimeFmt()
  const months = eachMonthOfInterval({ start, end })
  return (
    <div className="grid grid-cols-1 min-[480px]:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-6 gap-y-5">
      {months.map((m) => {
        const days = eachDayOfInterval({ start: startOfMonth(m), end: endOfMonth(m) })
        const lead = (getDay(days[0]) + 6) % 7
        const inMonth = days.filter((d) => !isBefore(d, start) && !isAfter(d, end)).map(iso)
        const present = inMonth.filter((k) => kinds.get(k) === 'present' || kinds.get(k) === 'late').length
        const leave = inMonth.filter((k) => kinds.get(k) === 'leave').length
        return (
          <div key={iso(m)}>
            <div className="flex items-baseline justify-between mb-1.5">
              <h4 className="text-sm font-semibold text-gray-800">{format(m, 'MMMM')}{months.length > 1 && m.getFullYear() !== start.getFullYear() ? ` ${format(m, 'yyyy')}` : ''}</h4>
              <span className="text-[10px] text-gray-400 tabular-nums">{present ? `${present} present` : ''}{leave ? ` · ${leave} leave` : ''}</span>
            </div>
            <div className="grid grid-cols-7 gap-[3px] text-center text-[9px] font-medium text-gray-300 mb-[3px]">
              {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => <div key={i}>{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-[3px]">
              {Array.from({ length: lead }).map((_, i) => <div key={`pad-${i}`} />)}
              {days.map((date) => {
                const key = iso(date)
                const outside = isBefore(date, start) || isAfter(date, end)
                const kind = outside ? 'norecord' : kinds.get(key)!
                const clickable = !outside && kind !== 'upcoming' && kind !== 'norecord'
                return (
                  <button key={key} disabled={!clickable} onClick={() => onPick(key)}
                    title={outside ? undefined : tooltip(key, kind, byDate.get(key), fmtTime, leaveLabel, holidayName.get(key))}
                    className={`aspect-square rounded-[4px] text-[9px] font-medium flex items-center justify-center ${outside ? 'opacity-30 bg-gray-50 text-gray-300' : KIND[kind].cell} ${clickable ? 'hover:ring-2 hover:ring-gray-400/60' : 'cursor-default'} ${key === today ? 'ring-2 ring-brand/70' : ''}`}>
                    {format(date, 'd')}
                  </button>
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
}

/* ─── List ──────────────────────────────────────────────────────────────────── */

function DayTable({ rows, kinds, byDate, today, fmtTime, onPick, leaveLabel, holidayName }: DayProps & { rows: string[]; fmtTime: (s?: string | null) => string }) {
  return (
    <div className="overflow-x-auto max-h-[36rem] overflow-y-auto">
      <table className="min-w-full text-sm">
        <thead className="sticky top-0 bg-gray-50 z-10">
          <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500">
            <th className="px-4 sm:px-5 py-2.5">Date</th>
            <th className="px-4 py-2.5">Status</th>
            <th className="px-4 py-2.5">Check in</th>
            <th className="px-4 py-2.5">Check out</th>
            <th className="px-4 py-2.5 text-right">Worked</th>
            <th className="px-4 sm:px-5 py-2.5 text-right">Notes</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((key) => {
            const kind = kinds.get(key)!
            const d = byDate.get(key)
            const notes = [
              d?.short_minutes ? `${hm(d.short_minutes)} short` : null,
              d?.late_minutes ? `${hm(d.late_minutes)} late` : null,
              d?.early_checkout_minutes ? `left ${hm(d.early_checkout_minutes)} early` : null,
            ].filter(Boolean).join(' · ')
            return (
              <tr key={key} onClick={() => onPick(key)} className={`cursor-pointer hover:bg-gray-50 ${key === today ? 'bg-brand/5' : ''}`}>
                <td className="px-4 sm:px-5 py-2.5 whitespace-nowrap">
                  <span className="font-medium text-gray-900">{format(parseISO(key), 'd MMM yyyy')}</span>
                  <span className="ml-2 text-xs text-gray-400">{format(parseISO(key), 'EEE')}</span>
                </td>
                <td className="px-4 py-2.5 whitespace-nowrap"><Badge kind={kind}>{statusText(kind, d, leaveLabel, holidayName.get(key))}</Badge></td>
                <td className="px-4 py-2.5 whitespace-nowrap tabular-nums text-gray-700">{fmtTime(d?.checkin_time)}</td>
                <td className="px-4 py-2.5 whitespace-nowrap tabular-nums text-gray-700">
                  {d?.is_active ? <span className={kind === 'missing' ? 'text-orange-600 text-xs font-medium' : 'text-green-600 text-xs font-medium'}>{kind === 'missing' ? 'Not checked out' : 'In progress'}</span> : fmtTime(d?.checkout_time)}
                </td>
                <td className="px-4 py-2.5 whitespace-nowrap text-right tabular-nums font-medium text-gray-900">{hm(d?.worked_minutes)}</td>
                <td className="px-4 sm:px-5 py-2.5 whitespace-nowrap text-right text-xs text-gray-500">{notes || '—'}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/* ─── Day detail ────────────────────────────────────────────────────────────── */

function DayDetail({ date, day, kind, fmtTime, leaveLabel, holiday, onClose }: {
  date: string; day?: MyAttendanceDay; kind: Kind; fmtTime: (s?: string | null) => string
  leaveLabel: DayProps['leaveLabel']; holiday?: string; onClose: () => void
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
  const rows: [string, string][] = [
    ['Shift', day?.expected_start && day?.expected_end ? `${day.expected_start} – ${day.expected_end}` : '—'],
    ['Check in', fmtTime(day?.checkin_time)],
    ['Check out', day?.is_active ? (kind === 'missing' ? 'Not checked out' : 'In progress') : fmtTime(day?.checkout_time)],
    ['Worked', hm(day?.worked_minutes)],
    ['Expected', hm(day?.expected_minutes)],
  ]
  if (day?.short_minutes) rows.push(['Short by', hm(day.short_minutes)])
  if (day?.late_minutes) rows.push(['Late by', hm(day.late_minutes)])
  if (day?.early_checkout_minutes) rows.push(['Left early by', hm(day.early_checkout_minutes)])
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className={`px-5 py-4 ${KIND[kind].cell}`}>
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-medium opacity-70">{format(parseISO(date), 'EEEE')}</p>
              <p className="text-xl font-bold">{format(parseISO(date), 'd MMMM yyyy')}</p>
            </div>
            <button onClick={onClose} className="opacity-60 hover:opacity-100" aria-label="Close"><X className="h-5 w-5" /></button>
          </div>
          <p className="mt-1 text-sm font-semibold">{statusText(kind, day, leaveLabel, holiday)}</p>
        </div>
        <dl className="divide-y divide-gray-100 px-5">
          {rows.map(([k, v]) => (
            <div key={k} className="flex justify-between py-2.5 text-sm">
              <dt className="text-gray-500">{k}</dt>
              <dd className="font-medium text-gray-900 tabular-nums">{v}</dd>
            </div>
          ))}
        </dl>
        {kind === 'missing' && (
          <p className="mx-5 mb-4 rounded-lg bg-orange-50 px-3 py-2 text-xs text-orange-800">
            You didn't check out this day. Raise a Check-in / Check-out request from the Requests page to fix it.
          </p>
        )}
        <div className="h-2" />
      </div>
    </div>
  )
}

